import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { EmptyState } from '../components/EmptyState';
import { Icon } from '../components/Icon';
import { PageHeader } from '../components/PageHeader';
import { Toast } from '../components/Toast';
import { type ComplianceCheckResult, type ComplianceRiskLevel, complianceApi } from '../lib/api';

export const Route = createFileRoute('/compliance')({
  component: CompliancePage,
});

const RISK_STYLE: Record<ComplianceRiskLevel, { label: string; cls: string; icon: string }> = {
  low: { label: '低风险', cls: 'bg-good/10 text-good border-good/30', icon: 'check' },
  medium: { label: '中风险', cls: 'bg-warn/10 text-warn border-warn/30', icon: 'alert-circle' },
  high: { label: '高风险', cls: 'bg-bad/10 text-bad border-bad/30', icon: 'alert-circle' },
};

const CATEGORY_LABEL: Record<string, string> = {
  safety: '安全',
  wireless: '无线',
  chemical: '化学',
  labeling: '标签',
  importer: '进口商',
  documentation: '单证',
  other: '其他',
};

function CompliancePage() {
  const [destination, setDestination] = useState('');
  const [product, setProduct] = useState('');
  const [targetMarket, setTargetMarket] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [result, setResult] = useState<ComplianceCheckResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  async function submit(e?: React.FormEvent) {
    e?.preventDefault();
    if (loading) return;
    if (!destination.trim() || !product.trim() || !targetMarket.trim()) {
      setError('请填写 目的地 / 产品 / 目标市场');
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const data = await complianceApi.check({
        destination: destination.trim(),
        product: product.trim(),
        targetMarket: targetMarket.trim(),
        productCategory: productCategory.trim() || undefined,
      });
      setResult(data);
      setToast('合规检查完成');
    } catch (err) {
      const e2 = err as { message?: string; body?: string };
      let msg = e2.message ?? '请求失败';
      if (e2.body) {
        try {
          const obj = JSON.parse(e2.body) as { message?: string };
          if (obj.message) msg = obj.message;
        } catch {
          // ignore
        }
      }
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  function reset() {
    setResult(null);
    setError(null);
  }

  return (
    <div>
      <PageHeader
        title="合规检查"
        description="输入目的地 + 产品 + 目标市场, AI 列出需要的认证/标签/进口商备案"
      />

      <form
        onSubmit={submit}
        className="bg-surface border border-line rounded-lg p-4 mb-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
      >
        <label className="block">
          <span className="text-xs text-ink-2 mb-1 block">目的地 (出口地) *</span>
          <input
            type="text"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="例: 东莞 / 上海 / 香港"
            className="w-full px-3 py-2 border border-line rounded text-sm"
            disabled={loading}
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-2 mb-1 block">产品 *</span>
          <input
            type="text"
            value={product}
            onChange={(e) => setProduct(e.target.value)}
            placeholder="例: 蓝牙音箱 / 软胶玩具 / 婴儿奶瓶"
            className="w-full px-3 py-2 border border-line rounded text-sm"
            disabled={loading}
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-2 mb-1 block">目标市场 *</span>
          <input
            type="text"
            value={targetMarket}
            onChange={(e) => setTargetMarket(e.target.value)}
            placeholder="例: 美国 / EU / 日本 / 英国"
            className="w-full px-3 py-2 border border-line rounded text-sm"
            disabled={loading}
          />
        </label>
        <label className="block">
          <span className="text-xs text-ink-2 mb-1 block">产品类目 (可选)</span>
          <input
            type="text"
            value={productCategory}
            onChange={(e) => setProductCategory(e.target.value)}
            placeholder="例: 玩具 / 食品接触 / 蓝牙电子"
            className="w-full px-3 py-2 border border-line rounded text-sm"
            disabled={loading}
          />
        </label>
        <div className="sm:col-span-2 flex items-center gap-2">
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-accent text-white rounded text-sm font-medium disabled:opacity-50"
          >
            {loading ? '检查中…' : '检查'}
          </button>
          {result && (
            <button
              type="button"
              onClick={reset}
              className="px-3 py-2 text-xs border border-line rounded hover:bg-surface-2"
            >
              清空
            </button>
          )}
        </div>
      </form>

      {error && (
        <div className="mb-4 px-3 py-2 text-sm text-bad bg-bad/10 border border-bad/30 rounded flex items-center gap-1.5">
          <Icon name="alert-triangle" size="sm" strokeWidth={2} />
          {error}
        </div>
      )}

      {!result && !loading && (
        <EmptyState
          icon="check"
          title="输入产品和目的地"
          description="AI 会基于合规知识库列出需要的认证/标签/进口商备案, 并标出风险等级"
        />
      )}

      {loading && !result && (
        <div className="bg-surface border border-line rounded p-8 text-center text-ink-3 text-sm">
          正在调 Workers AI 检索合规知识库…
        </div>
      )}

      {result && (
        <div className="space-y-4">
          {/* 风险等级 badge */}
          <div className="flex items-center gap-3 bg-surface border border-line rounded-lg p-4">
            <span className="text-xs text-ink-3">风险等级</span>
            <span
              className={`inline-flex items-center gap-1.5 px-3 py-1 rounded border text-sm font-medium ${
                RISK_STYLE[result.riskLevel].cls
              }`}
            >
              <Icon
                name={RISK_STYLE[result.riskLevel].icon}
                size="sm"
                strokeWidth={2}
                aria-hidden="true"
              />
              {RISK_STYLE[result.riskLevel].label}
            </span>
            <span className="text-xs text-ink-3 ml-auto">
              {result.checklist.length} 项检查 · {result.checklist.filter((c) => c.required).length}{' '}
              强制
            </span>
          </div>

          {/* checklist list */}
          <ul className="bg-surface border border-line rounded-lg divide-y divide-line">
            {result.checklist.map((c, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: AI-generated list, no stable id
              <li key={i} className="p-4 flex items-start gap-3">
                <div className="shrink-0 mt-0.5">
                  {c.required ? (
                    <span
                      className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded border bg-bad/10 text-bad border-bad/30"
                      title="强制"
                    >
                      必做
                    </span>
                  ) : (
                    <span
                      className="inline-block px-1.5 py-0.5 text-[10px] font-medium rounded border bg-info/10 text-info border-info/30"
                      title="建议"
                    >
                      建议
                    </span>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-sm font-medium text-ink">{c.item}</span>
                    <span className="text-[10px] uppercase tracking-wider text-ink-3 px-1.5 py-0.5 bg-surface-2 rounded">
                      {CATEGORY_LABEL[c.category] ?? c.category}
                    </span>
                  </div>
                  {c.deadline && (
                    <div className="text-xs text-ink-3 mt-0.5 flex items-center gap-1">
                      <Icon name="clock" size="xs" strokeWidth={2} />
                      {c.deadline}
                    </div>
                  )}
                  {c.note && <p className="text-xs text-ink-2 mt-1.5 leading-relaxed">{c.note}</p>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {toast && <Toast kind="success" message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
